import "@/styles/globals.css";
import "@/styles/AnimatedCarousel.scss";

import { Box, Center, Spinner, Text } from "@chakra-ui/react";
import { useJsApiLoader } from "@react-google-maps/api";
import { APIProvider } from "@vis.gl/react-google-maps";
import { Poppins } from "next/font/google";
import { SessionProvider, useSession } from "next-auth/react";
import { useStore } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";

import SessionRefresher from "@/components/auth/SessionRefresher";
import DisclaimerDialog from "@/components/general/DisclaimerDialog";
import { Provider } from "@/components/ui/provider";
import { Toaster } from "@/components/ui/toaster";
import { GOOGLE_MAPS_LIBRARIES, googleMapsApiKey } from "@/config/config";
import { DisclaimerProvider, useDisclaimer } from "@/context/disclaimerContext";
import { wrapper } from "@/hooks";
import Unauthorized from "@/sections/unauthorized/unauthorized";

const poppins = Poppins({ subsets: ["latin"], weight: ["400", "500", "600", "700", "800"] });

function App({ Component, pageProps: { session, ...pageProps } }) {
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: googleMapsApiKey,
        libraries: GOOGLE_MAPS_LIBRARIES,
    });

    const store = useStore((state) => state);

    return (
        <PersistGate loading={null} persistor={store.__persistor}>
            <SessionProvider
                session={session}
                // Re-fetch session every 15 minutes
                refetchInterval={15 * 60}
                // Re-fetches session when window is focused
                refetchOnWindowFocus={true}
            >
                <Provider forcedTheme="light">
                    <style jsx global>{`
                        :root {
                            font-family: ${poppins.style.fontFamily};
                        }
                        body {
                            font-family: ${poppins.style.fontFamily};
                        }
                    `}</style>
                    {isLoaded ? (
                        <APIProvider apiKey={googleMapsApiKey} libraries={GOOGLE_MAPS_LIBRARIES}>
                            <DisclaimerProvider>
                                <Auth>
                                    <SessionRefresher />
                                    <Component {...pageProps} />
                                    <Toaster />
                                    <GlobalDisclaimerDialog />
                                </Auth>
                            </DisclaimerProvider>
                        </APIProvider>
                    ) : null}
                </Provider>
            </SessionProvider>
        </PersistGate>
    );
}

function GlobalDisclaimerDialog() {
    const { showDisclaimer, acceptDisclaimer, hideDisclaimerDialog } = useDisclaimer();

    return (
        <DisclaimerDialog
            isOpen={showDisclaimer}
            onClose={hideDisclaimerDialog}
            onAccept={acceptDisclaimer}
        />
    );
}

function Auth({ children }) {
    // TODO: Handle all this logic in a middleware when migrating to App Router
    const { data: session, status } = useSession();

    // Show loading state while session is being fetched to prevent flashing the unauthorized page
    if (status === "loading") {
        return (
            <Box minH="100vh" display="flex" alignItems="center" justifyContent="center">
                <Center>
                    <Spinner size="lg" color="blue.500" />
                    <Text ml={3}>Cargando...</Text>
                </Center>
            </Box>
        );
    }

    const environment = process.env.NEXT_PUBLIC_ENV || "develop";
    const isProduction = environment == "production";

    if (isProduction || session?.roles?.includes("tech")) {
        return children;
    } else {
        return <Unauthorized />;
    }
}

export default wrapper.withRedux(App);
